import React, { useEffect, useMemo, useState } from "react";
import FormGeneric from "../../../../shared/components/generic/form/form-generic";
import { confirmSaveChanges } from "../../utils/admin-utils";
import AdminSection from "../../components/admin-section/admin-section";
import { paramsPactoRebatesFormConfig } from "../../config/parametros-pacto-rebates/pacto-rebates-params-form-config";
import AppButton from "../../../../shared/components/generic/button/app-button";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import { usePactoRebatesParams } from "../../hooks/parametros-pacto-rebates/usePactoRebatesParams";
import { usePactoRebatesParamsUpdate } from "../../hooks/parametros-pacto-rebates/usePactoRebatesParamsUpdate";
import {
	hasValidationErrors,
	removeClearedValidationErrors
} from "../../../utils/validation-utils";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";

const isConfirmAccepted = result => {
	return result?.isConfirmed === true || result?.value === true;
};

const ParametrosPactoRebates = () => {

	const [paramsActionLoadingMessage, setParamsActionLoadingMessage] = useState("");
	const [paramsValidationErrors, setParamsValidationErrors] = useState({});
	const [clearedValidationErrors, setClearedValidationErrors] = useState({});

	const {
		pactoRebatesParams,
		loading: pactoRebatesParamsLoading,
		refetch: fetchPactoRebatesParams
	} = usePactoRebatesParams();

	const {
		loading: updatePactoRebatesParamsLoading,
		validatePactoRebatesParams,
		updatePactoRebatesParams
	} = usePactoRebatesParamsUpdate();

	const currentParams = pactoRebatesParams?.[0] || null;

	const visibleParamsValidationErrors = removeClearedValidationErrors({
		errors: paramsValidationErrors,
		clearedErrors: clearedValidationErrors
	});

	const loadPactoRebatesParams = async () => {
		const result = await fetchPactoRebatesParams({
			page: 0,
			size: 10
		});

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os parâmetros do Pacto Rebates.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});
		}
	};

	useEffect(() => {
		loadPactoRebatesParams();
	}, []);

	const handleParamsFieldChange = ({ name }) => {
		setClearedValidationErrors(previous => ({
			...previous,
			[name]: true
		}));
	};

	const handleSubmitParamsPactoRebates = async values => {
		const validationErrors = validatePactoRebatesParams(values);

		if (hasValidationErrors(validationErrors)) {
			setClearedValidationErrors({});
			setParamsValidationErrors(validationErrors);

			await showCommonAlert({
				title: "Atenção",
				html: Object.values(validationErrors).join("<br />"),
				icon: "warning",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		const confirmResult = await confirmSaveChanges({
			description: "Deseja salvar as alterações?"
		});

		if (!isConfirmAccepted(confirmResult)) {
			return;
		}

		let updateResult;

		setParamsActionLoadingMessage("Salvando parâmetros Pacto Rebates...");

		try {
			updateResult = await updatePactoRebatesParams(values);

			if (updateResult.ok) {
				await loadPactoRebatesParams();
			}
		} finally {
			setParamsActionLoadingMessage("");
		}

		if (!updateResult.ok) {
			await showCommonAlert({
				title: "Erro",
				message: updateResult.error,
				fallbackMessage: "Não foi possível salvar os parâmetros do Pacto Rebates.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		setParamsValidationErrors({});
		setClearedValidationErrors({});

		await showCommonAlert({
			title: "Sucesso",
			message: "Parâmetros do Pacto Rebates atualizados com sucesso.",
			icon: "success",
			customClass: {
				container: "common-alert__container",
				confirmButton: "alert-form-button"
			}
		});
	};

	const handleCancelParamsPactoRebates = () => {
		setParamsValidationErrors({});
		setClearedValidationErrors({});
		loadPactoRebatesParams();
	};

	const formFields = useMemo(() => {
		return paramsPactoRebatesFormConfig({
			paramsPactoRebates: currentParams
		});
	}, [currentParams]);

	return (
		<>
			<PageLoading
				actionMessage={paramsActionLoadingMessage}
				states={[
					{
						loading: pactoRebatesParamsLoading,
						message: "Carregando parâmetros Pacto Rebates..."
					},
					{
						loading: updatePactoRebatesParamsLoading,
						message: "Salvando parâmetros Pacto Rebates..."
					}
				]}
			/>

			<AdminSection title="Configurações globais do programa Pacto Rebates">
				<FormGeneric
					formId="pacto-rebates-params-form"
					formFields={formFields}
					message={null}
					errors={visibleParamsValidationErrors}
					onSubmit={handleSubmitParamsPactoRebates}
					onFieldChange={handleParamsFieldChange}
				/>

				<div className="admin-form-actions">
					<AppButton
						type="button"
						onClick={handleCancelParamsPactoRebates}
						disabled={
							pactoRebatesParamsLoading ||
							updatePactoRebatesParamsLoading ||
							Boolean(paramsActionLoadingMessage)
						}
						className="admin-form-actions__button admin-form-actions__button--cancel"
					>
						Cancelar
					</AppButton>

					<AppButton
						type="submit"
						form="pacto-rebates-params-form"
						disabled={
							pactoRebatesParamsLoading ||
							updatePactoRebatesParamsLoading ||
							Boolean(paramsActionLoadingMessage)
						}
						className="admin-form-actions__button admin-form-actions__button--submit"
					>
						Salvar
					</AppButton>
				</div>
			</AdminSection>
		</>
	);
};

export default ParametrosPactoRebates;